import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { FeatureFlags, buildFeatureFlags } from "@/config/featureFlags";

interface FeatureFlagContextValue {
  flags: FeatureFlags;
  setFlag: (flag: keyof FeatureFlags, value: boolean) => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
  overrides?: Partial<FeatureFlags>;
  env?: Record<string, any>;
}

export const FeatureFlagProvider = ({ children, overrides, env }: FeatureFlagProviderProps) => {
  const [flags, setFlags] = useState<FeatureFlags>(() => buildFeatureFlags(env, overrides));

  const setFlag = useCallback((flag: keyof FeatureFlags, value: boolean) => {
    setFlags((prev) => ({ ...prev, [flag]: value }));
  }, []);

  const value = useMemo(() => ({ flags, setFlag }), [flags, setFlag]);

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context;
};

export const useFeatureFlag = (flag: keyof FeatureFlags) => {
  const { flags } = useFeatureFlags();
  return flags[flag];
};
